import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Badge,
    Box,
    Button,
    ButtonBase,
    Divider,
    IconButton,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import http from '../../services/http';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import { prazoLegivel, resumoDoAviso } from '../../utils/avisosTemporarios';

/**
 * O ícone dos avisos TEMPORÁRIOS, ao lado do sino.
 *
 * Pedido da Gisele (19/10/2026): "criar nos avisos também a possibilidade de
 * avisos temporários, que seriam na verdade avisos que expiram, e a expiração
 * é somente para que este aviso suma de um ícone temporário que deve ficar
 * logo ao lado do Sino no header que temos hoje, e isto é para avisos que são
 * mais importantes".
 *
 * ## O que ele NÃO é
 *
 * Não é o sino. O sino mostra notificação de trabalho (chamado atribuído,
 * mensagem nova) e cada uma é lida uma vez. Isto aqui é um recado da empresa
 * que vale por um tempo e some sozinho -- fica enquanto vale, para quem
 * chegar depois também ver.
 *
 * ## Some do ÍCONE, não do mural
 *
 * Passada a data, o aviso sai daqui e continua no mural, como qualquer outro.
 * Quem decide se expirou é o SERVIDOR (`/notice-board/destaques`): o relógio
 * do navegador do operador já produziu defeito nesta base.
 *
 * ## Por que o ícone some quando não há nada
 *
 * Porque ele é exceção, não mobília. Um ícone apagado no cabeçalho o dia
 * inteiro vira parte do fundo -- e aí, no dia em que acender, ninguém repara.
 */

/** De quanto em quanto tempo a lista é relida. */
export const INTERVALO_MS = 5 * 60 * 1000;

const AvisosTemporarios = () => {
    const { theme } = useContext(ThemeContext) || {};
    const history = useHistory();
    const [avisos, setAvisos] = useState([]);
    const [ancora, setAncora] = useState(null);

    const carregar = useCallback(async () => {
        try {
            const { data } = await http.get('/notice-board/destaques');
            setAvisos(Array.isArray(data) ? data : []);
        } catch (e) {
            // Silêncio de propósito: é um aviso de apoio no cabeçalho de toda
            // tela. Um toast de erro a cada cinco minutos, por uma lista que
            // não é o trabalho de ninguém, só atrapalharia.
            setAvisos([]);
        }
    }, []);

    useEffect(() => {
        carregar();
        const id = setInterval(carregar, INTERVALO_MS);

        return () => clearInterval(id);
    }, [carregar]);

    if (avisos.length === 0) return null;

    /**
     * Abre o mural -- no aviso clicado, quando houver um.
     *
     * O id vai na URL (`?aviso=`) e não no `state` da navegação porque assim
     * o endereço é compartilhável: o operador pode mandar o link do aviso
     * para um colega.
     */
    const abrirMural = (id = null) => {
        setAncora(null);
        history.push(id ? `/mural-avisos?aviso=${encodeURIComponent(id)}` : '/mural-avisos');
    };

    return (
        <>
            <Tooltip title="Avisos temporários" arrow placement="bottom">
                <Box>
                    <IconButton
                        size="small"
                        aria-label={`Avisos temporários (${avisos.length})`}
                        onClick={(e) => setAncora(e.currentTarget)}
                    >
                        <Badge badgeContent={avisos.length} color="error">
                            {/*
                              * Branco, e NÃO `text.primary` (ponto 36 da
                              * Gisele, 20/10: "melhorar a cor do ícone no modo
                              * claro").
                              *
                              * A barra do topo é `--sidebar-shell-bg`, que vale
                              * `#2a4d69` nos DOIS temas -- ela não acompanha o
                              * tema. `text.primary` acompanha: no modo claro
                              * vira quase preto, e o ícone sumia sobre o azul
                              * escuro. No escuro ficava claro por coincidência,
                              * e por isso o defeito só aparecia metade do
                              * tempo.
                              *
                              * O sino e o perfil ao lado já usam branco. Aqui
                              * fica `common.white`, que é a mesma cor pelo
                              * token -- o que importa é não depender do modo.
                              */}
                            <CampaignOutlined fontSize="small" sx={{ color: 'common.white' }} />
                        </Badge>
                    </IconButton>
                </Box>
            </Tooltip>

            <Popover
                open={Boolean(ancora)}
                anchorEl={ancora}
                onClose={() => setAncora(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 360, maxWidth: '96vw', maxHeight: 420 } } }}
            >
                <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Avisos temporários
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ overflowY: 'auto' }}>
                    {/*
                      * Cada aviso ABRE no mural, já naquele aviso (`?aviso=`).
                      * O resumo aqui é curto de propósito; quem quer o
                      * comunicado inteiro clica e lê onde ele mora, com anexo
                      * e formatação.
                      */}
                    {avisos.map((aviso) => (
                        <ButtonBase
                            key={aviso.id}
                            onClick={() => abrirMural(aviso.id)}
                            sx={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                px: 2,
                                py: 1.25,
                                borderBottom: `1px solid ${theme?.palette?.divider}`,
                                '&:hover': { backgroundColor: theme?.palette?.action?.hover },
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{aviso.title}</Typography>
                            {resumoDoAviso(aviso) && (
                                <Typography
                                    variant="caption"
                                    sx={{ color: theme?.palette?.text?.secondary, display: 'block', mt: 0.25 }}
                                >
                                    {resumoDoAviso(aviso)}
                                </Typography>
                            )}
                            <Typography
                                variant="caption"
                                sx={{ color: theme?.palette?.text?.disabled, display: 'block', mt: 0.5 }}
                            >
                                {prazoLegivel(aviso.expiresAt)}
                            </Typography>
                        </ButtonBase>
                    ))}
                </Box>
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={abrirMural} sx={{ textTransform: 'none' }}>
                        Abrir o mural
                    </Button>
                </Box>
            </Popover>
        </>
    );
};

export default AvisosTemporarios;
